import { Injectable, inject } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { LayerState, FlatLayerItem } from './layer-state';
import { LayerRepositoryProvider } from '../../../../shared/providers/repository-providers';
import { MapProvider } from '../../components/map/map-provider';
import { MapGridProvider } from '../../components/map-grid/map-grid-provider';
import { ScaleProvider } from '../../services/scale-provider';
import { Configuration } from '../../../../shared/configuration/configuration';
import { ArcGisLayerFactory } from '../../factories/arcgis-layer-factory';
import { LegendItemValueObject, LayerEntity } from '@gishub/domain';
import ArcGISMap from '@arcgis/core/Map';

@Injectable()
export class LayerNotifier extends BaseNotifier<LayerState> {
  private readonly _config = inject(Configuration);
  private readonly _layerFactory = inject(ArcGisLayerFactory);

  /**
   * Creación de capas en curso, por id.
   *
   * `syncMapLayers` se dispara desde el listener de estado y es asíncrono:
   * entre que se decide crear una capa y que se registra en el mapa pasa un
   * `await`. Si otro disparo del listener entra en esa ventana, `findLayerById`
   * sigue sin encontrar la capa y cada uno fabrica su propia instancia.
   *
   * Con el estado actualizándose varias veces seguidas (build, hydrateLayerScales,
   * recomputeOutOfScale, captureLayerScale) eso ocurría siempre: el mapa quedaba
   * con 27 ids duplicados y solo una instancia de cada pareja recibía vista de
   * capa. La que se dibuja no es la que devuelve `findLayerById`, así que el
   * TOC controlaba una capa invisible —de ahí que no se pudiera ver nada al
   * activarla— y la capa que sí se veía quedaba sin controlar.
   */
  private readonly _pendingLayers = new Map<string, Promise<any>>();

  /** Encadena los sync para que dos ejecuciones nunca se solapen. */
  private _syncQueue: Promise<void> = Promise.resolve();

  constructor(ref: Ref) {
    super(ref, {
      layers: [],
      loading: false,
      legends: {},
      defaultVisibleLayerIds: new Set(),
      searchText: '',
      expandedIds: new Set(),
      expandedSettingsIds: new Set(),
      outOfScaleIds: new Set(),
      unavailableIds: new Set(),
    });

    this.listen(() => {
      try {
        const { scale } = this.ref.watch(ScaleProvider).state();
        if (scale == null) return;
        this.recomputeOutOfScale(scale);
      } catch (e) {
        console.error('[LayerNotifier] Scale sync error:', e);
      }
    });

    this.listen(() => {
      try {
        const mapState = this.ref.watch(MapProvider).state();
        const layerState = this.state();

        // En modo Comparar (grid) cada vista gestiona sus propias capas:
        // el sync del TOC solo aplica al mapa principal de vista única.
        const gridState = this.ref.watch(MapGridProvider).state();
        if (gridState.activeViewsCount > 1) return;

        const map = mapState.map;
        const view = mapState.view;
        const layers = layerState?.layers;

        if (map && view && layers && layers.length > 0) {
          // Encadenado: el siguiente sync espera al anterior en vez de correr
          // en paralelo sobre el mismo mapa.
          this._syncQueue = this._syncQueue
            .then(() => this.syncMapLayers(map, layers))
            .catch(e => {
              console.error('[LayerNotifier] Sync error:', e);
            });
        }
      } catch (e) {
        console.error('[LayerNotifier] Sync error:', e);
      }
    });
  }

  private get layerRepo() {
    return this.ref.read(LayerRepositoryProvider).instance;
  }

  override async build(): Promise<void> {
    this.updateState(s => ({ ...s, loading: true }));
    try {
      const res = await this.layerRepo.getAll();
      const layers = res.data?.operationalLayers ?? [];
      const defaultIds = new Set<string>();
      const expanded = new Set<string>();

      const collect = (nodes: LayerEntity[]) => {
        for (const node of nodes) {
          if (node.visible) {
            if (node.layers) collect(node.layers);
            else defaultIds.add(node.id);
          }
          if (node.layers) {
            expanded.add(node.id);
            collect(node.layers);
          }
        }
      };
      collect(layers);

      this.setState({
        layers,
        loading: false,
        legends: {},
        defaultVisibleLayerIds: defaultIds,
        searchText: '',
        expandedIds: expanded,
        expandedSettingsIds: new Set(),
        outOfScaleIds: new Set(),
        unavailableIds: new Set(),
      });
      this.restoreLayersFromUrl();

      // Puebla minScale/maxScale de cada capa consultando el metadata del
      // servicio AL CONSTRUIRSE la capa (árbol del TOC). Reactivo: al llegar
      // los rangos se re-setear el estado y se recalcula outOfScaleIds con el
      // scale actual, de modo que el switch refleja el rango desde el inicio.
      void this.hydrateLayerScales();
    } catch (e) {
      console.error('[LayerNotifier] Error al cargar capas:', e);
      this.updateState(s => ({ ...s, loading: false }));
    }
  }

  /**
   * Puebla minScale/maxScale en cada capa hoja consultando el metadata del
   * servicio, de forma paralela. Se ejecuta al construir el árbol (build) para
   * que el switch del TOC refleje el rango de escala desde el inicio, sin
   * esperar a que la capa se cargue en el mapa ni a un zoom.
   *
   * Aprovecha la misma petición para detectar servicios caídos (error de red,
   * HTTP de error o body con `error`, p. ej. "Service not started") y marcar
   * la capa como no disponible en el TOC.
   */
  private async hydrateLayerScales(): Promise<void> {
    const leads: { node: LayerEntity; parent: LayerEntity[] }[] = [];
    const map = new Map<LayerEntity[], LayerEntity>();

    const walk = (nodes: LayerEntity[], parent?: LayerEntity[]) => {
      for (const node of nodes) {
        if (node.layers && node.layers.length) {
          walk(node.layers, node.layers);
          continue;
        }
        if (node.url) {
          map.set(parent ?? [], node);
          leads.push({ node, parent: parent ?? [] });
        }
      }
    };
    walk(this.snapshot().layers);

    const results = await Promise.all(
      leads.map(async ({ node }) => {
        try {
          const res = await this.layerRepo.getLayerMetadata(node.url!);
          const meta = res?.data;
          if (res?.success === false || meta?.error) {
            return { node, down: true as const, minScale: node.minScale, maxScale: node.maxScale };
          }
          const minScale = meta?.minScale > 0 ? meta.minScale : node.minScale;
          const maxScale = meta?.maxScale > 0 ? meta.maxScale : node.maxScale;
          return { node, down: false as const, minScale, maxScale };
        } catch {
          return { node, down: true as const, minScale: node.minScale, maxScale: node.maxScale };
        }
      }),
    );

    const unavailableIds = new Set(results.filter(r => r.down).map(r => r.node.id));

    let changed = false;
    const byNode = new Map(results.filter(r => !r.down).map(r => [r.node, r]));
    const patch = (nodes: LayerEntity[]): LayerEntity[] =>
      nodes.map(n => {
        if (n.layers && n.layers.length) return new LayerEntity({ ...n, layers: patch(n.layers) });
        const r = byNode.get(n);
        if (r && (r.minScale !== n.minScale || r.maxScale !== n.maxScale)) {
          changed = true;
          return new LayerEntity({ ...n, minScale: r.minScale, maxScale: r.maxScale });
        }
        return n;
      });

    this.updateState(s => ({ ...s, layers: patch(s.layers), unavailableIds }));

    if (changed) {
      const scale = this.ref.read(ScaleProvider).state().scale;
      if (scale != null) this.recomputeOutOfScale(scale);
    }
  }

  /**
   * Captura los rangos de escala (minScale/maxScale) que ArcGIS configura en la
   * capa al cargarla y los persiste en el entity, para que el TOC deshabilite/
   * apague el switch fuera de rango. Se re-evalúa tanto tras cargar el layer
   * como cuando ArcGIS actualiza estos valores.
   */
  private captureLayerScale(mapLayer: any, layerId: string): void {
    const persist = () => {
      if (this._disposed) return;
      const node = this.findLayerById(layerId);
      if (!node) return;
      const minScale = (mapLayer as any).minScale > 0 ? (mapLayer as any).minScale : undefined;
      const maxScale = (mapLayer as any).maxScale > 0 ? (mapLayer as any).maxScale : undefined;
      const targetMin = minScale ?? node.minScale;
      const targetMax = maxScale ?? node.maxScale;
      if (targetMin === node.minScale && targetMax === node.maxScale) return;

      let changed = false;
      const patch = (nodes: LayerEntity[]): LayerEntity[] =>
        nodes.map(n => {
          if (n.layers) return new LayerEntity({ ...n, layers: patch(n.layers) });
          if (n.id !== layerId) return n;
          changed = true;
          return new LayerEntity({ ...n, minScale: targetMin, maxScale: targetMax });
        });

      this.updateState(s => ({ ...s, layers: patch(s.layers) }));
      if (changed) {
        const scale = this.ref.read(ScaleProvider).state().scale;
        if (scale != null) this.recomputeOutOfScale(scale);
      }
    };

    const fromService = async () => {
      if (this._disposed) return;
      const node = this.findLayerById(layerId);
      if (!node?.url) return;
      try {
        const res = await this.layerRepo.getLayerMetadata(node.url);
        const meta = res?.data;
        if (meta) {
          (mapLayer as any).minScale = meta.minScale > 0 ? meta.minScale : (mapLayer as any).minScale;
          (mapLayer as any).maxScale = meta.maxScale > 0 ? meta.maxScale : (mapLayer as any).maxScale;
        }
      } catch {
        /* el fallback ArcGIS ya cubre el caso */
      }
      persist();
    };

    const w = mapLayer.when(() => {
      fromService();
    });
    if (w && typeof w.then === 'function') {
      w.then(() => fromService()).catch(() => fromService());
    }
    mapLayer.watch?.('minScale,maxScale', () => persist());
  }

  /**
   * Recalcula qué capas quedan fuera del rango de zoom actual (minScale/maxScale).
   */
  private recomputeOutOfScale(scale: number): void {
    const ids = new Set<string>();
    const walk = (nodes: LayerEntity[]) => {
      for (const node of nodes) {
        if (node.layers) { walk(node.layers); continue; }
        if (node.minScale != null && node.minScale > 0 && scale > node.minScale) ids.add(node.id);
        else if (node.maxScale != null && node.maxScale > 0 && scale < node.maxScale) ids.add(node.id);
      }
    };
    walk(this.snapshot().layers);
    this.updateState(s => ({ ...s, outOfScaleIds: ids }));
  }

  private restoreLayersFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const layersParam = params.get('layers');
    if (!layersParam) return;

    const layerIds = layersParam.split(',').map(s => s.trim()).filter(Boolean);
    for (const layerId of layerIds) {
      this.toggleLayerVisibility(layerId, true);
    }
  }

  /**
   * Devuelve la capa de `node` ya registrada en el mapa, creándola si hace
   * falta. El registro en `_pendingLayers` es lo que garantiza que una id se
   * cree una sola vez: mientras su creación está en curso, quien la pida espera
   * esa misma promesa en lugar de fabricar una segunda instancia.
   */
  private async resolveMapLayer(map: ArcGISMap, node: LayerEntity): Promise<any | null> {
    const existente = map.findLayerById(node.id) as any;
    if (existente) return existente;

    const enCurso = this._pendingLayers.get(node.id);
    if (enCurso) return enCurso;

    const tarea = (async (): Promise<any | null> => {
      // Reintento: entre la comprobación inicial y el registro, otro sync pudo
      // haber creado la capa.
      const yaRegistrada = map.findLayerById(node.id) as any;
      if (yaRegistrada) return yaRegistrada;

      const creada = await this._layerFactory.createLayer(node);
      if (!creada) return null;

      // `createLayer` es asíncrono: en este punto otra instancia del mismo id
      // pudo haberse añadido. Nunca se registra una segunda.
      const duplicada = map.findLayerById(node.id) as any;
      if (duplicada) return duplicada;

      map.add(creada);
      this.captureLayerScale(creada, node.id);
      return creada;
    })();

    this._pendingLayers.set(node.id, tarea);
    try {
      return await tarea;
    } finally {
      this._pendingLayers.delete(node.id);
    }
  }

  private async syncMapLayers(map: ArcGISMap, nodes: LayerEntity[], parentVisible = true): Promise<void> {
    for (const node of nodes) {
      const effectiveVisible = parentVisible && node.visible;

      if (node.type === 'GroupLayer' && node.layers) {
        await this.syncMapLayers(map, node.layers, effectiveVisible);
        continue;
      }

      if (node.url) {
        const mapLayer = (await this.resolveMapLayer(map, node)) as any;

        if (mapLayer) {
          if (mapLayer.visible !== effectiveVisible) {
            mapLayer.visible = effectiveVisible;
          }
          if (mapLayer.opacity !== (node.opacity ?? 1)) {
            mapLayer.opacity = node.opacity ?? 1;
          }
          if (mapLayer.hasOwnProperty('definitionExpression')) {
            const newExpr = node.definitionQuery || undefined;
            if (mapLayer.definitionExpression !== newExpr) {
              mapLayer.definitionExpression = newExpr;
            }
          }
          if (mapLayer.sublayers && mapLayer.type === 'map-image') {
            const sublayerId = node.url?.match(/\/MapServer\/(\d+)/i)?.[1];
            if (sublayerId) {
              const sub = mapLayer.sublayers.find((s: any) => s.id === Number(sublayerId));
              if (sub) sub.visible = effectiveVisible;
            }
          }
        }
      }
    }
  }

  setSearchText(text: string): void {
    this.updateState(s => ({ ...s, searchText: text }));
  }

  toggleGroup(id: string): void {
    this.updateState(s => {
      const next = new Set(s.expandedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...s, expandedIds: next };
    });
  }

  toggleSettings(id: string): void {
    const s = this.snapshot();
    const isOpening = !s.expandedSettingsIds.has(id);
    this.updateState(state => {
      const next = new Set(state.expandedSettingsIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...state, expandedSettingsIds: next };
    });

    if (isOpening) {
      const layer = this.findLayerById(id);
      if (layer) this.loadLegend(layer);
    }
  }

  get filteredLayers(): LayerEntity[] {
    const s = this.snapshot();
    const layers = s.layers;
    const term = s.searchText.toLowerCase().trim();
    if (!term) return layers;

    const filterNode = (node: LayerEntity): LayerEntity | null => {
      const match = node.name?.toLowerCase().includes(term);
      if (node.layers) {
        const filtered = node.layers.map(n => filterNode(n)).filter((n): n is LayerEntity => n != null);
        if (match || filtered.length > 0) return new LayerEntity({ ...node, layers: filtered });
      }
      return match ? node : null;
    };
    return layers.map(n => filterNode(n)).filter((n): n is LayerEntity => n != null);
  }

  get flatTree(): FlatLayerItem[] {
    const s = this.snapshot();
    const items: FlatLayerItem[] = [];
    const walk = (nodes: LayerEntity[], depth: number) => {
      for (const node of nodes) {
        const isGroup = !!node.layers;
        items.push({ node, depth, isGroup });
        if (isGroup && s.expandedIds.has(node.id)) walk(node.layers!, depth + 1);
      }
    };
    walk(this.filteredLayers, 0);
    return items;
  }

  getGroupVisibleCount(node: LayerEntity): number {
    if (!node.layers) return node.visible ? 1 : 0;
    return node.layers.reduce((sum, c) => sum + this.getGroupVisibleCount(c), 0);
  }

  countLeaves(node: LayerEntity): number {
    if (!node.layers) return 1;
    return node.layers.reduce((sum, c) => sum + this.countLeaves(c), 0);
  }

  get isAllVisible(): boolean {
    const check = (nodes: LayerEntity[]): boolean => nodes.length > 0 && nodes.every(n =>
      n.visible && (!n.layers || check(n.layers))
    );
    return check(this.snapshot().layers);
  }

  toggleAllVisibility(): void {
    this.setAllLayersVisibility(!this.isAllVisible);
  }

  isLayerOutOfScale(layer: LayerEntity): boolean {
    const scale = this.ref.read(ScaleProvider).state().scale;
    if (scale == null) return false;
    if (layer.minScale != null && layer.minScale > 0 && scale > layer.minScale) return true;
    if (layer.maxScale != null && layer.maxScale > 0 && scale < layer.maxScale) return true;
    return false;
  }

  private mapTree(nodes: LayerEntity[], id: string, fn: (node: LayerEntity) => LayerEntity): LayerEntity[] {
    return nodes.map(node => {
      if (node.id === id) return fn(node);
      if (node.layers) return new LayerEntity({ ...node, layers: this.mapTree(node.layers, id, fn) });
      return node;
    });
  }

  private setDeepVisible(node: LayerEntity, visible: boolean): LayerEntity {
    return new LayerEntity({
      ...node, visible,
      layers: node.layers?.map(child => this.setDeepVisible(child, visible)),
    });
  }

  private setNodeWithAncestorsVisible(nodes: LayerEntity[], targetId: string): { result: LayerEntity[]; found: boolean } {
    let found = false;
    const result = nodes.map(node => {
      if (node.id === targetId) { found = true; return new LayerEntity({ ...node, visible: true }); }
      if (node.layers) {
        const { result: children, found: childFound } = this.setNodeWithAncestorsVisible(node.layers, targetId);
        if (childFound) { found = true; return new LayerEntity({ ...node, visible: true, layers: children }); }
        return new LayerEntity({ ...node, layers: children });
      }
      return node;
    });
    return { result, found };
  }

   toggleLayerVisibility(layerId: string, visible: boolean): void {
     // Capas con servicio caído no se activan: el switch aparece deshabilitado
     // con la etiqueta "Servicio no disponible".
     if (visible && this.snapshot().unavailableIds.has(layerId)) return;
     this.updateState(s => ({
       ...s,
       layers: visible
         ? this.setNodeWithAncestorsVisible(s.layers, layerId).result
         : this.mapTree(s.layers, layerId, node => new LayerEntity({ ...node, visible: false })),
     }));
   }

   toggleCategoryVisibility(categoryId: string, visible: boolean): void {
     this.updateState(s => {
       const layers = this.mapTree(s.layers, categoryId, cat => this.setDeepVisible(cat, visible));
       return {
         ...s,
         layers: visible ? this.setNodeWithAncestorsVisible(layers, categoryId).result : layers,
       };
     });
   }

   async loadLegend(layer: LayerEntity): Promise<void> {
    if (!layer.url || this.snapshot().legends[layer.id]) return;

    // La leyenda del servicio describe el renderer del servidor. Si la capa se dibuja con un
    // renderer de cliente (densidad, agrupamiento, flujo animado) lo que se ve es otra cosa,
    // así que no se pide: el TOC dibuja su propia muestra (ver `layer-component`).
    if (layer.hasClientRender) return;

    const match = layer.url.match(/^(https?:\/\/.*?\/MapServer)(?:\/(\d+))?/i);
    if (!match) return;
    const baseUrl = match[1];

    const legendUrl = `${baseUrl}/legend?f=json`;
    let fetchUrl = legendUrl;

    if (layer.hasProxy) {
      const cleanProxy = this._config.proxyUrl
        .replace(/\?.*$/, '')
        .replace(/\/+$/, '');
      fetchUrl = `${cleanProxy}?${legendUrl}`;
    }

    try {
      const res = await this.layerRepo.getLegend(fetchUrl, match[2]);
      if (res.success && res.data && res.data.length > 0) {
        const items: LegendItemValueObject[] = res.data;
        this.updateState(s => ({
          ...s,
          legends: { ...s.legends, [layer.id]: items },
        }));
      }
    } catch (e) {
      console.warn('[LayerNotifier] Error al cargar leyenda:', e);
    }
  }

  zoomToLayer(id: string): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map || !mapState.view) return;
    const layer = mapState.map.findLayerById(id) as any;
    if (!layer) return;
    layer.when(() => {
      if (layer.fullExtent) mapState.view!.goTo(layer.fullExtent).catch(() => {});
    });
  }

  setLayerOpacity(layerId: string, opacity: number): void {
    const clamped = Math.max(0, Math.min(1, opacity));
    if (Number.isNaN(clamped)) return;

    this.updateState(s => ({
      ...s,
      layers: this.mapTree(s.layers, layerId, node => new LayerEntity({ ...node, opacity: clamped })),
    }));

    const mapLayer = this.ref.read(MapProvider).state().map?.findLayerById(layerId) as any;
    if (mapLayer) mapLayer.opacity = clamped;
  }

  setOrder(parentKey: string | null | undefined, orderedIds: string[]): void {
    const order: Record<string, number> = {};
    orderedIds.forEach((id, i) => {
      order[id] = i;
    });
    const apply = (nodes: LayerEntity[]): LayerEntity[] =>
      [...nodes].sort((a, b) => (order[a.id] ?? Number.MAX_SAFE_INTEGER) - (order[b.id] ?? Number.MAX_SAFE_INTEGER));

    this.updateState(s => ({
      ...s,
      layers:
        parentKey == null
          ? apply(s.layers)
          : this.mapTree(s.layers, parentKey, node => new LayerEntity({ ...node, layers: apply(node.layers ?? []) })),
    }));
  }

  setAllLayersVisibility(visible: boolean): void {
    const defaults = this.snapshot().defaultVisibleLayerIds;
    const setAll = (nodes: LayerEntity[]): LayerEntity[] =>
      nodes.map(node => {
        if (node.layers) {
          const children = setAll(node.layers);
          const hasAnyVisible = children.some(l => l.visible);
          return new LayerEntity({ ...node, visible: hasAnyVisible, layers: children });
        }
        return new LayerEntity({ ...node, visible: visible || defaults.has(node.id) });
      });
    this.updateState(s => ({ ...s, layers: setAll(s.layers) }));
  }

  private findLayerById(id: string): LayerEntity | null {
    const walk = (nodes: LayerEntity[]): LayerEntity | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.layers) {
          const found = walk(node.layers);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(this.snapshot().layers);
  }
}
